import { useValue } from "@legendapp/state/react";
import type { MentionPartData } from "@repo/shared/types/mention-part";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Icon } from "@repo/ui/components/icon";
import { cn } from "@repo/ui/lib/utils";

import { blocks$ } from "~/stores/blocks/blocks.crud";
import { boards$ } from "~/stores/boards/boards.crud";
import { datasets$ } from "~/stores/datasets/datasets.crud";
import { datasources$ } from "~/stores/datasources/datasources.crud";

const DEFAULT_CLASS = "size-3 shrink-0";

/**
 * Renders the leading icon for a mention — used by both the editor pill (via
 * the Tiptap NodeView), the historical chip in past messages, and the popover
 * row, so all three surfaces stay in sync with the live entity state.
 *
 * - `block` / `board` / `dataset` — user-set `avatar` falls back to the kind icon.
 * - `datasource` — `Icon.DatasourceType` from the connection type
 *   (postgres, mysql, …) falls back to `stack-rounded` when the entity isn't
 *   in the local store (e.g. deleted upstream).
 *
 * All Legend State subscriptions are read unconditionally to satisfy React's
 * rules of hooks; the `kind` discriminator picks which one renders.
 */
export const MentionPillIcon = ({
  id,
  kind,
  className,
}: {
  id: string;
  kind: MentionPartData["kind"];
  className?: string;
}) => {
  const blockAvatar = useValue(blocks$[id]?.avatar);
  const boardAvatar = useValue(boards$[id]?.avatar);
  const datasetAvatar = useValue(datasets$[id]?.avatar);
  const datasourceType = useValue(datasources$[id]?.type);
  const datasourceManaged = useValue(datasources$[id]?.managed);
  const cls = cn(DEFAULT_CLASS, className);

  if (kind === "block") {
    return (
      <AvatarDisplay
        avatar={blockAvatar}
        fallbackIcon="blocks"
        className={cls}
      />
    );
  }
  if (kind === "board") {
    return (
      <AvatarDisplay
        avatar={boardAvatar}
        fallbackIcon="dashboard"
        className={cls}
      />
    );
  }
  if (kind === "dataset") {
    return (
      <AvatarDisplay
        avatar={datasetAvatar}
        fallbackIcon="dataset"
        className={cls}
      />
    );
  }
  if (datasourceType) {
    return (
      <Icon.DatasourceType
        datasourceType={datasourceType}
        managed={datasourceManaged}
        className={cls}
      />
    );
  }
  return (
    <Icon name="stack-rounded" className={cn(cls, "text-muted-foreground")} />
  );
};
